/**
 * Device-level auth state.
 *
 * On boot the provider fetches `/api/session` to decide whether to show the
 * login screen or the app. Status is one of:
 *
 *  - "pending"          — boot fetch in flight
 *  - "unauthenticated"  — no valid cookie; show LoginScreen
 *  - "authenticated"    — cookie valid; render the app
 *
 * "Parent mode" (the in-app gated area, §3.2) is a separate concern handled
 * in HomeScreen / the parent-area routing — not in this context. This
 * context only tracks the underlying device session.
 */
/* eslint-disable react-refresh/only-export-components --
 * Co-locating the provider, context, and `useAuth` hook in one file is the
 * idiomatic React pattern. Fast Refresh still works in practice because the
 * non-component exports don't change at edit-time. */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

import { api } from "@/lib/api";

export type AuthStatus = "pending" | "unauthenticated" | "authenticated";

export type LoginOutcome =
  | { ok: true }
  | { ok: false; reason: "invalid_password" | "network" | "bad_request" | "server_misconfigured" };

interface AuthContextValue {
  status: AuthStatus;
  login: (password: string) => Promise<LoginOutcome>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>("pending");

  const refresh = useCallback(async () => {
    try {
      const { status: httpStatus, body } = await api.getSession();
      if (httpStatus === 200 && body?.authenticated) {
        setStatus("authenticated");
      } else {
        setStatus("unauthenticated");
      }
    } catch {
      // Treat network failure on boot as unauthenticated so we show the
      // login prompt rather than spinning forever. The user can retry by
      // re-submitting the password.
      setStatus("unauthenticated");
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(async (password: string): Promise<LoginOutcome> => {
    try {
      const { status: httpStatus, body } = await api.login(password);
      if (httpStatus === 200) {
        setStatus("authenticated");
        return { ok: true };
      }
      if (httpStatus === 401) return { ok: false, reason: "invalid_password" };
      if (httpStatus === 400) return { ok: false, reason: "bad_request" };
      if (
        httpStatus === 500 &&
        body !== null &&
        typeof body === "object" &&
        "error" in body &&
        (body as { error?: unknown }).error === "server_misconfigured"
      ) {
        return { ok: false, reason: "server_misconfigured" };
      }
      return { ok: false, reason: "network" };
    } catch {
      return { ok: false, reason: "network" };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } finally {
      setStatus("unauthenticated");
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ status, login, logout, refresh }),
    [status, login, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be called inside <AuthProvider>");
  return ctx;
}
